import React, { useCallback, useEffect, useState } from 'react';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { AlertCircle, CheckCircle2, Loader2, Stethoscope, X } from 'lucide-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import PhoneInput from '@/components/ui/phone-input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { practitionerService } from '@/services/practitionerService';
import { clinicAdminService } from '@/services/clinicAdminService';
import { stateService } from '@/services/stateService';
import { toast } from '@/hooks/use-toast';
import { getDEAValidationError } from '@/lib/validators';
import { State } from '@/models/state.model';
import PlacesAutocomplete from '@/components/ui/google-places-autocomplete';

// Schema for making admin a practitioner
const makeAdminPractitionerSchema = z.object({
  npi_number: z
    .string()
    .trim()
    .regex(/^\d{10}$/, 'NPI number must be 10 digits'),
  credentials: z.string().trim().optional().or(z.literal('')),
  license_number: z
    .string()
    .trim()
    .max(30, 'License number must be at most 30 characters')
    .optional()
    .or(z.literal('')),
  dea_number: z
    .string()
    .trim()
    .optional()
    .refine(
      (value) => {
        if (!value || value === '') return true;
        const error = getDEAValidationError(value);
        return error === null;
      },
      (value) => ({
        message: getDEAValidationError(value || '') || 'Invalid DEA number',
      })
    ),
  phone: z
    .string()
    .trim()
    .max(20, 'Phone must be at most 20 characters')
    .optional()
    .or(z.literal('')),
  fax: z.string().trim().max(20, 'Fax must be at most 20 characters').optional().or(z.literal('')),
  address_line_1: z
    .string()
    .trim()
    .min(5, 'Please enter a valid address')
    .max(100, 'Address must be at most 100 characters'),
  address_line_2: z
    .string()
    .trim()
    .max(100, 'Address line 2 must be at most 100 characters')
    .optional()
    .or(z.literal('')),
  city: z
    .string()
    .trim()
    .min(2, 'Please enter a valid city')
    .max(50, 'City must be at most 50 characters'),
  state: z.string().trim().length(2, 'Please select a state'),
  postal_code: z
    .string()
    .trim()
    .regex(/^\d{5}$/, 'Postal code must be 5 digits'),
});

type MakeAdminPractitionerFormData = z.infer<typeof makeAdminPractitionerSchema>;

interface AdminData {
  id: string;
  user_id: string;
  first_name: string;
  last_name: string;
  email: string;
}

interface MakeAdminPractitionerSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  clinicId: string;
  admin: AdminData | null;
}

// Credential options for practitioners
const credentialOptions = [
  { value: 'MD', label: 'MD - Doctor of Medicine' },
  { value: 'DO', label: 'DO - Doctor of Osteopathic Medicine' },
  { value: 'NP', label: 'NP - Nurse Practitioner' },
  { value: 'PA', label: 'PA - Physician Assistant' },
  { value: 'DVM', label: 'DVM - Doctor of Veterinary Medicine' },
  { value: 'APRN', label: 'APRN - Advanced Practice Registered Nurse' },
  { value: 'CNM', label: 'CNM - Certified Nurse Midwife' },
  { value: 'DNP', label: 'DNP - Doctor of Nursing Practice' },
];

const MakeAdminPractitionerSheet: React.FC<MakeAdminPractitionerSheetProps> = ({
  open,
  onOpenChange,
  onSuccess,
  clinicId,
  admin,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [states, setStates] = useState<State[]>([]);
  const [npiValidation, setNpiValidation] = useState<{
    isValidating: boolean;
    exists: boolean;
    practitioner?: { id: string; name: string; npi: string };
  }>({
    isValidating: false,
    exists: false,
  });

  const form = useForm<MakeAdminPractitionerFormData>({
    resolver: zodResolver(makeAdminPractitionerSchema),
    defaultValues: {
      npi_number: '',
      credentials: '',
      license_number: '',
      dea_number: '',
      phone: '',
      fax: '',
      address_line_1: '',
      address_line_2: '',
      city: '',
      state: '',
      postal_code: '',
    },
  });

  // Fetch states on mount
  useEffect(() => {
    const fetchStates = async () => {
      try {
        const response = await stateService.getAllStates();
        if (response.data?.states) {
          setStates(response.data.states);
        }
      } catch (error) {
        console.error('Failed to fetch states:', error);
      }
    };
    fetchStates();
  }, []);

  // Reset form when sheet opens/closes
  useEffect(() => {
    if (open) {
      form.reset();
      setNpiValidation({ isValidating: false, exists: false });
    }
  }, [open, form]);

  // Debounced NPI validation
  const validateNpi = useCallback(async (npi: string) => {
    if (!npi || npi.length !== 10 || !/^\d{10}$/.test(npi)) {
      setNpiValidation({ isValidating: false, exists: false });
      return;
    }

    setNpiValidation((prev) => ({ ...prev, isValidating: true }));

    try {
      const response = await practitionerService.checkPractitionerNpiExists(npi);
      // Response structure: { success, data: { exists, practitioner } } or { exists, practitioner }
      const result = response?.data || response;
      setNpiValidation({
        isValidating: false,
        exists: result?.exists || false,
        practitioner: result?.practitioner,
      });

      // Set error if NPI already exists - we don't allow linking to existing practitioners
      if (result?.exists) {
        form.setError('npi_number', {
          type: 'manual',
          message: 'This NPI is already registered to another practitioner. Please use a unique NPI.',
        });
      } else {
        form.clearErrors('npi_number');
      }
    } catch (error) {
      console.error('NPI validation error:', error);
      setNpiValidation({ isValidating: false, exists: false });
    }
  }, [form]);

  // Watch NPI changes for validation
  useEffect(() => {
    const npi = form.watch('npi_number');
    if (npi && npi.length === 10) {
      setNpiValidation((prev) => ({ ...prev, isValidating: true }));
    }
    const timeoutId = setTimeout(() => {
      if (npi && npi.length === 10) {
        validateNpi(npi);
      } else {
        setNpiValidation({ isValidating: false, exists: false });
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [form.watch('npi_number'), validateNpi]);

  // Handle address selection from Google Places
  const handleAddressSelect = (addressData: any) => {
    if (!addressData) return;

    form.setValue('address_line_1', addressData.address_line_1 || '', {
      shouldValidate: true,
      shouldDirty: true,
    });
    form.setValue('address_line_2', addressData.address_line_2 || '', {
      shouldDirty: true,
    });
    form.setValue('city', addressData.city || '', {
      shouldValidate: true,
      shouldDirty: true,
    });

    if (addressData.state) {
      const matchedState = states.find(
        (s) => s.code === addressData.state || s.name.toLowerCase() === addressData.state.toLowerCase()
      );
      if (matchedState) {
        form.setValue('state', matchedState.code, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
    }

    if (addressData.postal_code) {
      form.setValue('postal_code', addressData.postal_code, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  };

  const handleSubmit = async (formData: MakeAdminPractitionerFormData) => {
    if (!admin) return;

    try {
      setIsSubmitting(true);

      if (npiValidation.isValidating) {
        toast({
          title: 'Please wait',
          description: 'NPI validation is in progress. Please wait a moment and try again.',
          variant: 'destructive',
        });
        return;
      }

      // Block submission if NPI already exists
      if (npiValidation.exists) {
        toast({
          title: 'NPI Already Exists',
          description: 'This NPI is already registered to another practitioner. Please use a unique NPI.',
          variant: 'destructive',
        });
        return;
      }

      const requestData = {
        npi_number: formData.npi_number,
        credentials: formData.credentials || undefined,
        license_number: formData.license_number || undefined,
        dea_number: formData.dea_number || undefined,
        phone: formData.phone || undefined,
        fax: formData.fax || undefined,
        address_line_1: formData.address_line_1,
        address_line_2: formData.address_line_2 || undefined,
        city: formData.city,
        state: formData.state,
        postal_code: formData.postal_code,
      };

      await clinicAdminService.makeAdminPractitioner(clinicId, admin.id, requestData);

      toast({
        title: 'Success',
        description: `${admin.first_name} ${admin.last_name} has been registered as a practitioner.`,
        variant: 'success',
      });

      form.reset();
      onOpenChange(false);
      onSuccess();
    } catch (error: any) {
      console.error('Error making admin a practitioner:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to make admin a practitioner. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        className="w-full !max-w-[640px] rounded-l-2xl p-0 flex flex-col gap-0 [&>button]:hidden"
        onInteractOutside={(e) => {
          const target = e.target as HTMLElement;
          if (target?.closest('.pac-container')) {
            e.preventDefault();
          }
        }}
        onPointerDownOutside={(e) => {
          const target = e.target as HTMLElement;
          if (target?.closest('.pac-container')) {
            e.preventDefault();
          }
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <Stethoscope className="h-5 w-5 text-emerald-600" />
            <h2 className="text-base font-semibold text-slate-900">Make Admin a Practitioner</h2>
          </div>
          <button
            onClick={() => onOpenChange(false)}
            className="w-10 h-10 rounded-full border border-slate-200 flex items-center justify-center hover:bg-slate-50 transition-colors"
          >
            <X className="h-5 w-5 text-slate-600" />
          </button>
        </div>

        {/* Content */}
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="flex flex-col flex-1 overflow-hidden">
            <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
              {/* Admin Info (Read-only) */}
              <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                <h3 className="text-xs font-semibold uppercase text-slate-500">Admin Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-slate-500">First Name</label>
                    <p className="text-sm font-medium text-slate-900">{admin?.first_name || '-'}</p>
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Last Name</label>
                    <p className="text-sm font-medium text-slate-900">{admin?.last_name || '-'}</p>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-slate-500">Email</label>
                  <p className="text-sm font-medium text-slate-900">{admin?.email || '-'}</p>
                </div>
              </div>

              {/* NPI Number */}
              <FormField
                control={form.control}
                name="npi_number"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Practitioner NPI <span className="text-red-500">*</span>
                    </FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          placeholder="Enter 10-digit NPI number"
                          maxLength={10}
                          {...field}
                          onChange={(e) => {
                            setNpiValidation((prev) => ({ ...prev, isValidating: true }));
                            const value = e.target.value.replace(/\D/g, '');
                            field.onChange(value);
                          }}
                          className={npiValidation.exists ? 'border-red-500' : ''}
                        />
                        {npiValidation.isValidating && (
                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                          </div>
                        )}
                        {npiValidation.exists && !npiValidation.isValidating && (
                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            <AlertCircle className="h-4 w-4 text-red-500" />
                          </div>
                        )}
                        {!npiValidation.exists && !npiValidation.isValidating && field.value?.length === 10 && (
                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                          </div>
                        )}
                      </div>
                    </FormControl>
                    <FormMessage />
                    {!npiValidation.isValidating && npiValidation.exists && npiValidation.practitioner && (
                      <div className="flex items-start gap-2 p-2 bg-red-50 border border-red-200 rounded-md mt-2">
                        <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
                        <div className="text-xs text-red-700">
                          <p className="font-medium">NPI Already Registered</p>
                          <p>
                            This NPI is already registered to <strong>{npiValidation.practitioner.name}</strong>.
                            Each admin must have their own unique NPI to be registered as a practitioner.
                          </p>
                        </div>
                      </div>
                    )}
                    {!npiValidation.isValidating && !npiValidation.exists && field.value?.length === 10 && (
                      <p className="text-xs text-emerald-600 mt-1">
                        NPI is available - a new practitioner profile will be created.
                      </p>
                    )}
                  </FormItem>
                )}
              />

              {/* Only show additional fields if NPI doesn't exist (new practitioner) */}
              {!npiValidation.isValidating && !npiValidation.exists && form.watch('npi_number')?.length === 10 && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    {/* Credentials */}
                    <FormField
                      control={form.control}
                      name="credentials"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Credentials</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select credentials" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {credentialOptions.map((option) => (
                                <SelectItem key={option.value} value={option.value}>
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* License Number */}
                    <FormField
                      control={form.control}
                      name="license_number"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>License Number</FormLabel>
                          <FormControl>
                            <Input placeholder="Enter license number" maxLength={30} {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    {/* DEA Number */}
                    <FormField
                      control={form.control}
                      name="dea_number"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>DEA Number</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Enter DEA number (optional)"
                              {...field}
                              onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                              maxLength={20}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                  {/* Phone */}
                  <PhoneInput
                    form={form}
                    name="phone"
                    label="Phone"
                    placeholder="Enter phone number"
                    required={false}
                  />
                  </div>

                  {/* Address Section */}
                  <div className="space-y-4 border-t border-slate-200 pt-4">
                    <h3 className="text-xs font-semibold uppercase text-slate-500">Office Address</h3>

                    {/* Google Places Autocomplete */}
                    <div className="relative z-50">
                      <PlacesAutocomplete onSelectPlace={handleAddressSelect} />
                    </div>

                    <FormField
                      control={form.control}
                      name="address_line_1"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            Address Line 1 <span className="text-red-500">*</span>
                          </FormLabel>
                          <FormControl>
                            <Input placeholder="Enter street address" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="address_line_2"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Address Line 2</FormLabel>
                          <FormControl>
                            <Input placeholder="Suite, floor, etc. (optional)" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="grid grid-cols-3 gap-4">
                      <FormField
                        control={form.control}
                        name="city"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              City <span className="text-red-500">*</span>
                            </FormLabel>
                            <FormControl>
                              <Input placeholder="Enter city" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="state"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              State <span className="text-red-500">*</span>
                            </FormLabel>
                            <Select onValueChange={field.onChange} value={field.value}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {states.map((state) => (
                                  <SelectItem key={state.id} value={state.code}>
                                    {state.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="postal_code"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              Postal Code <span className="text-red-500">*</span>
                            </FormLabel>
                            <FormControl>
                              <Input
                                placeholder="12345"
                                maxLength={5}
                                {...field}
                                onChange={(e) => {
                                  const value = e.target.value.replace(/\D/g, '');
                                  field.onChange(value);
                                }}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between">
              <Button
                type="button"
                variant="ghost"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
                className="text-slate-600 hover:text-slate-900"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-brand-teal hover:bg-brand-teal/90 text-white"
                disabled={
                  isSubmitting
                  || npiValidation.isValidating
                  || npiValidation.exists
                  || !form.watch('npi_number')
                  || form.watch('npi_number').length !== 10
                }
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Make Practitioner'
                )}
              </Button>
            </div>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
};

export default MakeAdminPractitionerSheet;
